import { t } from '@lingui/macro';
import {
  FlowUnitsAPIType,
  PegasusFlowStatusesModel,
  PegasusTimeStatusesModel,
  PegasusTimeStatusesAPIModel,
  TimeOfDay,
  PegasusAdvancedTimeHolidayModel,
  PegasusAdvancedTimeModel,
  PegasusAdvancedTimeAPIModel,
  PegasusAdvancedTimeHolidayAPIModel,
  PegasusFlowStatusesAPIModel,
  PressureUnitsAPIType,
} from '@shared/models';
import { ModeSettingsTabsType } from './ModeSettings/ModeSettings.utils';

//MODELS and ENUMS

export interface UseStateModel<T> {
  data: T;
  setData: React.Dispatch<React.SetStateAction<T>>;
}

export enum AdvancedTimeTableDaysType {
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
  Sunday = 'Sunday',
  Holiday = 'Holiday',
}

export const AdvancedTimeTableDaysFn = (): Record<AdvancedTimeTableDaysType, string> => ({
  [AdvancedTimeTableDaysType.Monday]: t`Monday`,
  [AdvancedTimeTableDaysType.Tuesday]: t`Tuesday`,
  [AdvancedTimeTableDaysType.Wednesday]: t`Wednesday`,
  [AdvancedTimeTableDaysType.Thursday]: t`Thursday`,
  [AdvancedTimeTableDaysType.Friday]: t`Friday`,
  [AdvancedTimeTableDaysType.Saturday]: t`Saturday`,
  [AdvancedTimeTableDaysType.Sunday]: t`Sunday`,
  [AdvancedTimeTableDaysType.Holiday]: t`Holiday`,
});

export enum TimeLearningTableType {
  Regular = 'Regular',
  Advanced = 'Advanced',
}

export const TimeLearningTableFn = (): Record<TimeLearningTableType, string> => ({
  [TimeLearningTableType.Regular]: t({
    message: 'Regular Table',
    id: '[TableType.Regular]',
  }),
  [TimeLearningTableType.Advanced]: t({
    message: 'Advanced Time Tables',
    id: '[TableType.Advanced]',
  }),
});

export enum ControlFlagsType {
  DontUseTimeTable = 'DontUseTimeTable',
  HighestPressureWins = 'HighestPressureWins',
  LowestPressureWins = 'LowestPressureWins',
  UseTimeTableOnFlowErrorOnly = 'UseTimeTableOnFlowErrorOnly',
}

export const ControlFlagsFn = (): Record<ControlFlagsType, string> => ({
  [ControlFlagsType.DontUseTimeTable]: t({
    message: `Don't use time table`,
    id: '[ControlFlags.DontUseTimeTable]',
  }),
  [ControlFlagsType.HighestPressureWins]: t({
    message: 'Use pressure from time table if higher than flow table',
    id: '[ControlFlags.HighestPressureWins]',
  }),
  [ControlFlagsType.LowestPressureWins]: t({
    message: 'Use pressure from time table if lower than flow table',
    id: '[ControlFlags.LowestPressureWins]',
  }),
  [ControlFlagsType.UseTimeTableOnFlowErrorOnly]: t({
    message: 'Use pressure from time table if flow is invalid',
    id: '[ControlFlags.UseTimeTableOnFlowErrorOnly]',
  }),
});

export interface TablesAndLearningDataSource {
  flowTable: UseStateModel<PegasusFlowStatusesModel[]>;
  timeTable: UseStateModel<PegasusTimeStatusesModel[]>;
  advanced: UseStateModel<PegasusAdvancedTimeModel[]>;
  advancedHoliday: UseStateModel<PegasusAdvancedTimeHolidayModel[]>;
  overrideFlowTable: UseStateModel<PegasusFlowStatusesModel[]>;
  overrideTimeTable: UseStateModel<PegasusTimeStatusesModel[]>;
  tableType: UseStateModel<TimeLearningTableType>;
  offsetsTable: UseStateModel<MultipleFlowTableOffsetsModel[]>;
  learningModeComplete: boolean;
}

export interface LearningModeModel {
  learningDataLimit: number;
  learningModeComplete: boolean;
}

export const DstSummerOffsetOptions = [0, 0.5, 1, 1.5, 2] as const;
export const StandardSummerOffsetOptions = [0, 1, 2, 3] as const;
export const SummerOffsetOptions = [...DstSummerOffsetOptions, ...StandardSummerOffsetOptions] as const;
export type SummerOffsetType = (typeof SummerOffsetOptions)[number];

export enum DstDayOfWeekType {
  OnExactDates,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
  Sunday,
}

export const DstDayOfWeekFn = (): Record<DstDayOfWeekType, string> => ({
  [DstDayOfWeekType.OnExactDates]: t`On exact dates`,
  [DstDayOfWeekType.Monday]: t`On the first Monday after dates`,
  [DstDayOfWeekType.Tuesday]: t`On the first Tuesday after dates`,
  [DstDayOfWeekType.Wednesday]: t`On the first Wednesday after dates`,
  [DstDayOfWeekType.Thursday]: t`On the first Thursday after dates`,
  [DstDayOfWeekType.Friday]: t`On the first Friday after dates`,
  [DstDayOfWeekType.Saturday]: t`On the first Saturday after dates`,
  [DstDayOfWeekType.Sunday]: t`On the first Sunday after dates`,
});

export interface SummerTimeOffsetModel {
  summerOffset: SummerOffsetType;
  summerStart?: Date;
  summerEnd?: Date;
  //
  dstDayOfWeek?: DstDayOfWeekType;
  dstCallFunctionEnabled?: boolean;
  dstConsumptionEnabled?: boolean;
}

export interface MultipleFlowTableOffsetsModel {
  time: string;
  offset: number;
}

export interface MultipleFlowTableOffsetsAPIModel {
  time: TimeOfDay;
  offset: number;
}

export enum FlowInvalidToUseType {
  UseFixedOutlet = 'UseFixedOutlet',
  UseTimeTable = 'UseTimeTable',
}

export const FlowInvalidToUseFn = (): Record<FlowInvalidToUseType, string> => ({
  [FlowInvalidToUseType.UseFixedOutlet]: t({
    message: 'Use Fixed Outlet',
    id: '[InvalidFlows.UseFixedOutlet]',
  }),
  [FlowInvalidToUseType.UseTimeTable]: t({
    message: 'Use Time Table',
    id: '[InvalidFlows.UseTimeTable]',
  }),
});

export interface InvalidFlowsModel {
  useZeroFlows: boolean;
  faultTimeout?: number; // int
  useFlowBandLimits: boolean;
  minValidFlow?: number;
  maxValidFlow?: number;
  invalidFlowOverride: FlowInvalidToUseType;
  faultPressure?: number;
  loggerCalibrationFactor?: number;
}

export interface DeadBandControlModel {
  deadBand?: number;
  deadBandLimit?: number;
}

export interface ActuatorLimitsModel {
  actuatorMin?: number;
  actuatorMax?: number;
}

export interface SingleFlowTableOffsetModel {
  nightOffset?: number;
  nightStartMins?: string;
  nightEndMins?: string;
}

export type LatchControlType =
  | 'LatchOnHighUnreachable'
  | 'LatchOnLowUnreachable'
  | 'NoLatch'
  | 'LatchVentOverride'
  | 'LatchCloseOverride';

export const LatchControlFn = (): Record<LatchControlType, string> => ({
  LatchOnHighUnreachable: t({
    message: 'Latch On High Unreachable (Fully open PRV)',
    id: '[LatchMode.OnHighUnreachable]',
  }),
  LatchOnLowUnreachable: t({
    message: 'Latch On Low Unreachable (Fully close PRV)',
    id: '[LatchMode.OnLowUnreachable]',
  }),
  NoLatch: t({ message: 'No Latch', id: '[LatchMode.NoLatch]' }),
  LatchVentOverride: t({
    message: 'Manual Latch High Override (Fully open PRV)',
    id: '[LatchMode.VentOverride]',
  }),
  LatchCloseOverride: t({
    message: 'Manual Latch Low Override (Fully close PRV)',
    id: '[LatchMode.CloseOverride]',
  }),
});

export interface ControllerAlarmsModel {
  minInletPres?: number;
  maxInletPres?: number;
  minFlow?: number;
  maxFlow?: number;
  minOutletPres?: number;
  maxOutletPres?: number;
  minTopPres?: number;
  maxTopPres?: number;
}

export enum MultipleCPsType {
  Off = 'Off',
  Cp2Between = 'Cp2Between',
  Min2Cp = 'Min2Cp',
  Min3Cp = 'Min3Cp',
}

export const MultipleCPsFn = (): Record<MultipleCPsType, string> => ({
  [MultipleCPsType.Off]: t({ message: 'Off', id: '[CPControl.Off]' }),
  [MultipleCPsType.Cp2Between]: t({ message: 'CP2', id: '[CPControl.Cp2Between]' }),
  [MultipleCPsType.Min2Cp]: t({ message: 'Minimum of 2', id: '[CPControl.Min2Cp]' }),
  [MultipleCPsType.Min3Cp]: t({ message: 'Minimum of 3', id: '[CPControl.Min3Cp]' }),
});

export interface CpControlModel {
  cpMin?: number;
  cpBand?: number;
  minValidCp?: number;
  maxValidCp?: number;
  cpMode: MultipleCPsType;
  cpNightStartMins?: number;
  cpNightEndMins?: number;
}

export interface RecalculateBodyModel {
  pressureUnit: PressureUnitsAPIType;
  flowUnit: FlowUnitsAPIType;
  learningDataLimit: number;
  cpMin: number;
  maxValidCp: number;
  minValidCp: number;
}

export interface GeneralSettingsFormModel
  extends DeadBandControlModel,
    ActuatorLimitsModel,
    SingleFlowTableOffsetModel,
    ControllerAlarmsModel,
    CpControlModel {
  latchMode: LatchControlType;
  solenoidActuationThreshold?: number;
}

export interface SettingsFormModel extends GeneralSettingsFormModel {
  prvId?: number;
  cpId?: number;
  cpChannelId?: number;
  cp2Id?: number;
  cp2ChannelId?: number;
  cp3Id?: number;
  cp3ChannelId?: number;

  pressureUnit?: PressureUnitsAPIType;
  flowUnit?: FlowUnitsAPIType;

  // note
  note?: string;

  // mode selection
  controlMode: ModeSettingsTabsType;
  bigTimeTableEnabled: boolean;

  // invalid flow section (V)
  useZeroFlows: boolean;
  faultTimeout?: number; // int
  useFlowBandLimits: boolean;
  minValidFlow?: number;
  maxValidFlow?: number;
  invalidFlowOverride: FlowInvalidToUseType;
  faultPressure?: number;

  // overrides
  holOn: boolean;
  holStart?: Date; // date
  holEnd?: Date; // date

  // regular time table
  controlFlags: ControlFlagsType;

  // fixed outlet (V)
  cpTarget?: number;

  // summer time offset (V)
  summerStart?: Date; // date
  summerEnd?: Date; // date
  summerOffset: SummerOffsetType; // int
  //
  dstDayOfWeek?: DstDayOfWeekType;
  dstCallFunctionEnabled?: boolean;
  dstConsumptionEnabled?: boolean;

  // - - - - LEARNING - - - - (V)
  learningDataLimit?: number;
}

export interface SettingsPostModel
  extends Omit<
    SettingsFormModel,
    'holStart' | 'holEnd' | 'summerStart' | 'summerEnd' | 'nightStartMins' | 'nightEndMins'
  > {
  // regular flow table
  flowTable: PegasusFlowStatusesAPIModel[];

  // regular time table
  timeTable: PegasusTimeStatusesAPIModel[];

  // overrides
  overrideFlowTable: PegasusFlowStatusesAPIModel[];
  overrideTimeTable: PegasusTimeStatusesAPIModel[];
  holStart?: string; // date
  holEnd?: string; // date

  // single flow table offset
  nightStartMins?: number; // mins from midnight
  nightEndMins?: number; // mins from midnight

  // pressure offset table
  offsetsTable: MultipleFlowTableOffsetsAPIModel[];

  // advanced tables
  bigTimeTable: PegasusAdvancedTimeAPIModel[];
  bigTimeHolidayTable: PegasusAdvancedTimeHolidayAPIModel[]; // date as date

  // summer time offset (V)
  summerStart?: string; // date
  summerEnd?: string; // date
}

export interface ModeSettingsGetModel
  extends Omit<
    SettingsPostModel,
    'holStart' | 'holEnd' | 'summerStart' | 'summerEnd' | 'bigTimeHolidayTable' | 'nightStartMins' | 'nightEndMins'
  > {
  learningModeComplete?: boolean;
  // additional data
  alarmOffset?: number; // ?
  prvPressureBeyondActuator?: boolean; // action - maybe toast to display (if true)

  holStart?: string; // date
  holEnd?: string; // date

  // single flow table offset
  nightStartMins?: number; // mins from midnight
  nightEndMins?: number; // mins from midnight

  summerStart?: string; // date
  summerEnd?: string; // date

  bigTimeHolidayTable: PegasusAdvancedTimeHolidayAPIModel[]; // date as string
}
